import React, { useCallback, useEffect, useState } from "react";

import Toast from "../../components/Toast";
import { GET_CERAMIC_LOTS_URL, getData } from "../../utils/api";
import CreateSettlementModal from "./CreateSettlementModal";

import "./insights.css";
import "./lot-hedge-settlement.css";

const PAGE_SIZE = 25;
let SEARCH_TIMER = null;

const VARIANCE_OPTIONS = [
  { value: "all", suffix: "All" },
  { value: "any", suffix: "Any" },
  { value: "pos", suffix: "+" },
  { value: "neg", suffix: "−" },
  { value: "zero", suffix: "Zero" },
];

function varianceOptions(prefix) {
  return VARIANCE_OPTIONS.map((opt) => ({
    value: opt.value,
    label: `${prefix}: ${opt.suffix}`,
  }));
}

const PT_OZ_VARIANCE_FILTERS = varianceOptions("Pt var");
const PD_OZ_VARIANCE_FILTERS = varianceOptions("Pd var");
const RH_OZ_VARIANCE_FILTERS = varianceOptions("Rh var");
const PAYMENT_VARIANCE_FILTERS = varianceOptions("$ var");

function formatOz(n) {
  if (n == null || Number.isNaN(Number(n))) return "—";
  return Number(n).toFixed(2);
}

function formatUsd(n) {
  if (n == null || Number.isNaN(Number(n))) return "—";
  return Number(n).toLocaleString("en-US", {
    style: "currency",
    currency: "USD",
    maximumFractionDigits: 0,
  });
}

function formatDate(ms) {
  if (!ms) return "—";
  const d = new Date(Number(ms));
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleDateString("en-US", { year: "numeric", month: "short", day: "numeric" });
}

function formatSignedOz(n) {
  if (n == null || Number.isNaN(Number(n))) return "—";
  const v = Number(n);
  const abs = Math.abs(v).toFixed(2);
  if (Math.abs(v) < 0.00001) return "0.00";
  return v > 0 ? `+${abs}` : `−${abs}`;
}

function formatSignedUsd(n) {
  if (n == null || Number.isNaN(Number(n))) return "—";
  const v = Number(n);
  const abs = Math.abs(v).toLocaleString("en-US", {
    style: "currency",
    currency: "USD",
    maximumFractionDigits: 0,
  });
  if (Math.abs(v) < 0.005) return "$0";
  return v > 0 ? `+${abs}` : `−${abs}`;
}

function varianceClass(n) {
  if (n == null || Number.isNaN(Number(n)) || Math.abs(Number(n)) < 0.00001) return "";
  return Number(n) > 0 ? " is-pos" : " is-neg";
}

const LotHedgeSettlementV2 = ({ showCreateSettlement = false, onCloseCreateSettlement }) => {
  const [lots, setLots] = useState([]);
  const [total, setTotal] = useState(0);
  const [openCount, setOpenCount] = useState(0);
  const [settledCount, setSettledCount] = useState(0);
  const [matchedCash, setMatchedCash] = useState({
    ourCeramicAmount: 0,
    partnerPaid: 0,
    cashGap: 0,
    recoveryPct: 0,
    negativePaymentVar: 0,
    negativePtVar: 0,
    negativePdVar: 0,
    negativeRhVar: 0,
  });
  const [lotTotals, setLotTotals] = useState({
    pt: 0,
    pd: 0,
    rh: 0,
    ceramicAmount: 0,
  });
  const [hasMore, setHasMore] = useState(false);
  const [offset, setOffset] = useState(0);
  const [query, setQuery] = useState("");
  const [searchInput, setSearchInput] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [ptOzVarianceFilter, setPtOzVarianceFilter] = useState("all");
  const [pdOzVarianceFilter, setPdOzVarianceFilter] = useState("all");
  const [rhOzVarianceFilter, setRhOzVarianceFilter] = useState("all");
  const [paymentVarianceFilter, setPaymentVarianceFilter] = useState("all");
  const [isLoading, setIsLoading] = useState(true);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [toastParams, setToastParams] = useState({ message: "", type: "", id: 0 });

  const closeCreateSettlement = () => {
    if (onCloseCreateSettlement) onCloseCreateSettlement();
  };

  const toggleStatusFilter = (next) => {
    setStatusFilter((prev) => (prev === next ? "all" : next));
  };

  const loadLots = useCallback(
    async ({
      text = "",
      skip = 0,
      append = false,
      settlementStatus = "all",
      ptOzVariance = "all",
      pdOzVariance = "all",
      rhOzVariance = "all",
      paymentVariance = "all",
    } = {}) => {
      try {
        if (append) setIsLoadingMore(true);
        else setIsLoading(true);

        const params = {
          count: PAGE_SIZE,
          offset: skip,
          settlementStatus,
          ptOzVariance,
          pdOzVariance,
          rhOzVariance,
          paymentVariance,
        };
        const q = (text || "").trim();
        if (q) params.text = q;

        const data = await getData(GET_CERAMIC_LOTS_URL, params);
        const payload = data?.data || data || {};
        const list = payload.list || [];

        setLots((prev) => (append ? [...prev, ...list] : list));
        setTotal(Number(payload.total) || 0);
        setOpenCount(Number(payload.openCount) || 0);
        setSettledCount(Number(payload.settledCount) || 0);
        setMatchedCash({
          ourCeramicAmount: Number(payload.matchedOurCeramicAmount) || 0,
          partnerPaid: Number(payload.matchedPartnerPaid) || 0,
          cashGap: Number(payload.matchedCashGap) || 0,
          recoveryPct: Number(payload.matchedPaymentRecoveryPct) || 0,
          negativePaymentVar: Number(payload.matchedNegativePaymentVar) || 0,
          negativePtVar: Number(payload.matchedNegativePtVar) || 0,
          negativePdVar: Number(payload.matchedNegativePdVar) || 0,
          negativeRhVar: Number(payload.matchedNegativeRhVar) || 0,
        });
        setLotTotals({
          pt: Number(payload.totalPtOz) || 0,
          pd: Number(payload.totalPdOz) || 0,
          rh: Number(payload.totalRhOz) || 0,
          ceramicAmount: Number(payload.totalCeramicAmount) || 0,
        });
        setHasMore(Boolean(payload.areMoreItemsAvailable));
        setOffset(skip + list.length);
      } catch (e) {
        if (!append) {
          setLots([]);
          setOpenCount(0);
          setSettledCount(0);
          setMatchedCash({
            ourCeramicAmount: 0,
            partnerPaid: 0,
            cashGap: 0,
            recoveryPct: 0,
            negativePaymentVar: 0,
            negativePtVar: 0,
            negativePdVar: 0,
            negativeRhVar: 0,
          });
          setLotTotals({ pt: 0, pd: 0, rh: 0, ceramicAmount: 0 });
        }
        setHasMore(false);
        setToastParams({
          type: "ERROR",
          message: e && e.error ? e.error : "Failed to load ceramic lots",
          id: Date.now(),
        });
      } finally {
        setIsLoading(false);
        setIsLoadingMore(false);
      }
    },
    []
  );

  useEffect(() => {
    loadLots({
      text: query,
      skip: 0,
      append: false,
      settlementStatus: statusFilter,
      ptOzVariance: ptOzVarianceFilter,
      pdOzVariance: pdOzVarianceFilter,
      rhOzVariance: rhOzVarianceFilter,
      paymentVariance: paymentVarianceFilter,
    });
  }, [
    query,
    statusFilter,
    ptOzVarianceFilter,
    pdOzVarianceFilter,
    rhOzVarianceFilter,
    paymentVarianceFilter,
    loadLots,
  ]);

  const handleSearchChange = (e) => {
    const value = e.target.value;
    setSearchInput(value);
    if (SEARCH_TIMER) clearTimeout(SEARCH_TIMER);
    SEARCH_TIMER = setTimeout(() => {
      setQuery(value.trim());
    }, 300);
  };

  return (
    <>
      <div className="lot-hedge-page">
        <div className="lot-hedge-summary lot-hedge-summary--v2">
          <button
            type="button"
            className={`lot-hedge-summary-card lot-hedge-summary-card--clickable${
              statusFilter === "all" ? " is-active" : ""
            }`}
            onClick={() => setStatusFilter("all")}
          >
            <div className="lot-hedge-summary-label">Ceramic lots</div>
            <div className="lot-hedge-summary-value">
              {isLoading ? "…" : openCount + settledCount}
            </div>
          </button>
          <button
            type="button"
            className={`lot-hedge-summary-card lot-hedge-summary-card--warn lot-hedge-summary-card--clickable${
              statusFilter === "PENDING" ? " is-active" : ""
            }`}
            onClick={() => toggleStatusFilter("PENDING")}
          >
            <div className="lot-hedge-summary-label">Open lots</div>
            <div className="lot-hedge-summary-value">{isLoading ? "…" : openCount}</div>
          </button>
          <button
            type="button"
            className={`lot-hedge-summary-card lot-hedge-summary-card--ok lot-hedge-summary-card--clickable${
              statusFilter === "SETTLED" ? " is-active" : ""
            }`}
            onClick={() => toggleStatusFilter("SETTLED")}
          >
            <div className="lot-hedge-summary-label">Settled lots</div>
            <div className="lot-hedge-summary-value">{isLoading ? "…" : settledCount}</div>
          </button>
          <div className="lot-hedge-summary-card">
            <div className="lot-hedge-summary-label">Pt oz</div>
            <div className="lot-hedge-summary-value">
              {isLoading ? "…" : formatOz(lotTotals.pt)}
            </div>
          </div>
          <div className="lot-hedge-summary-card">
            <div className="lot-hedge-summary-label">Pd oz</div>
            <div className="lot-hedge-summary-value">
              {isLoading ? "…" : formatOz(lotTotals.pd)}
            </div>
          </div>
          <div className="lot-hedge-summary-card">
            <div className="lot-hedge-summary-label">Rh oz</div>
            <div className="lot-hedge-summary-value">
              {isLoading ? "…" : formatOz(lotTotals.rh)}
            </div>
          </div>
          <div className="lot-hedge-summary-card">
            <div className="lot-hedge-summary-label">Ceramic amount</div>
            <div className="lot-hedge-summary-value">
              {isLoading ? "…" : formatUsd(lotTotals.ceramicAmount)}
            </div>
          </div>
        </div>

        {settledCount > 0 && (
          <div className="lot-hedge-summary lot-hedge-summary--matched-cash">
            <div className="lot-hedge-summary-card">
              <div className="lot-hedge-summary-label">Our ceramic $</div>
              <div className="lot-hedge-summary-value">
                {isLoading ? "…" : formatUsd(matchedCash.ourCeramicAmount)}
              </div>
              <div className="lot-hedge-summary-hint">
                Matched lots · booked from invoices
              </div>
            </div>
            <div className="lot-hedge-summary-card">
              <div className="lot-hedge-summary-label">Partner paid</div>
              <div className="lot-hedge-summary-value">
                {isLoading ? "…" : formatUsd(matchedCash.partnerPaid)}
              </div>
              <div className="lot-hedge-summary-hint">Prelim + final payment</div>
            </div>
            <div className="lot-hedge-summary-card">
              <div className="lot-hedge-summary-label">Cash gap</div>
              <div
                className={`lot-hedge-summary-value${varianceClass(matchedCash.cashGap)}`}
              >
                {isLoading ? "…" : formatSignedUsd(matchedCash.cashGap)}
              </div>
              <div className="lot-hedge-summary-hint">
                {isLoading ? "…" : `${matchedCash.recoveryPct.toFixed(1)}% recovery`}
              </div>
            </div>
            <div className="lot-hedge-summary-card">
              <div className="lot-hedge-summary-label">−$ var</div>
              <div
                className={`lot-hedge-summary-value${varianceClass(
                  matchedCash.negativePaymentVar
                )}`}
              >
                {isLoading ? "…" : formatSignedUsd(matchedCash.negativePaymentVar)}
              </div>
              <div className="lot-hedge-summary-hint">
                Sum of negative $ var only
              </div>
            </div>
            <div className="lot-hedge-summary-card">
              <div className="lot-hedge-summary-label">−Pt var</div>
              <div
                className={`lot-hedge-summary-value${varianceClass(
                  matchedCash.negativePtVar
                )}`}
              >
                {isLoading ? "…" : formatSignedOz(matchedCash.negativePtVar)}
              </div>
              <div className="lot-hedge-summary-hint">Sum of negative Pt oz var</div>
            </div>
            <div className="lot-hedge-summary-card">
              <div className="lot-hedge-summary-label">−Pd var</div>
              <div
                className={`lot-hedge-summary-value${varianceClass(
                  matchedCash.negativePdVar
                )}`}
              >
                {isLoading ? "…" : formatSignedOz(matchedCash.negativePdVar)}
              </div>
              <div className="lot-hedge-summary-hint">Sum of negative Pd oz var</div>
            </div>
            <div className="lot-hedge-summary-card">
              <div className="lot-hedge-summary-label">−Rh var</div>
              <div
                className={`lot-hedge-summary-value${varianceClass(
                  matchedCash.negativeRhVar
                )}`}
              >
                {isLoading ? "…" : formatSignedOz(matchedCash.negativeRhVar)}
              </div>
              <div className="lot-hedge-summary-hint">Sum of negative Rh oz var</div>
            </div>
          </div>
        )}

        <div className="lot-hedge-v2-toolbar">
          <div className="lot-hedge-v2-toolbar-left">
            <input
              type="search"
              className="lot-hedge-v2-search"
              placeholder="Search lot name…"
              value={searchInput}
              onChange={handleSearchChange}
              aria-label="Search ceramic lot name"
            />
            <select
              className="lot-hedge-v2-filter"
              value={ptOzVarianceFilter}
              onChange={(e) => setPtOzVarianceFilter(e.target.value)}
              aria-label="Filter by Pt ounce variance"
            >
              {PT_OZ_VARIANCE_FILTERS.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
            <select
              className="lot-hedge-v2-filter"
              value={pdOzVarianceFilter}
              onChange={(e) => setPdOzVarianceFilter(e.target.value)}
              aria-label="Filter by Pd ounce variance"
            >
              {PD_OZ_VARIANCE_FILTERS.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
            <select
              className="lot-hedge-v2-filter"
              value={rhOzVarianceFilter}
              onChange={(e) => setRhOzVarianceFilter(e.target.value)}
              aria-label="Filter by Rh ounce variance"
            >
              {RH_OZ_VARIANCE_FILTERS.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
            <select
              className="lot-hedge-v2-filter"
              value={paymentVarianceFilter}
              onChange={(e) => setPaymentVarianceFilter(e.target.value)}
              aria-label="Filter by payment variance"
            >
              {PAYMENT_VARIANCE_FILTERS.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
          </div>
          <span className="lot-hedge-v2-count">
            {isLoading ? "Loading…" : `${lots.length} of ${total} lots`}
          </span>
        </div>

        <div className="lot-hedge-table-wrap lot-hedge-v2-table-wrap">
          <table className="lot-hedge-table">
            <thead>
              <tr>
                <th>Lot name</th>
                <th>Status</th>
                <th>Pt oz</th>
                <th>Pd oz</th>
                <th>Rh oz</th>
                <th>Ceramic amount</th>
                <th>Partner paid</th>
                <th>Pt var</th>
                <th>Pd var</th>
                <th>Rh var</th>
                <th>$ var</th>
                <th>Invoices</th>
                <th>Latest</th>
              </tr>
            </thead>
            <tbody>
              {isLoading && lots.length === 0 ? (
                <tr>
                  <td colSpan={13} className="lot-hedge-v2-empty">
                    Loading ceramic lots…
                  </td>
                </tr>
              ) : lots.length === 0 ? (
                <tr>
                  <td colSpan={13} className="lot-hedge-v2-empty">
                    No ceramic lots found
                    {query ? ` for “${query}”` : ""}.
                  </td>
                </tr>
              ) : (
                lots.map((lot) => {
                  const status = (lot.settlementStatus || "PENDING").toUpperCase();
                  const isSettled = status === "SETTLED";
                  return (
                    <tr key={lot.lotName}>
                      <td>
                        <div className="lot-hedge-lot-id">{lot.lotName}</div>
                      </td>
                      <td>
                        <span
                          className={`lot-hedge-status-pill${
                            isSettled ? " lot-hedge-status-pill--settled" : " lot-hedge-status-pill--pending"
                          }`}
                        >
                          {isSettled ? "Settled" : "Pending"}
                        </span>
                      </td>
                      <td>{formatOz(lot.ptOz)}</td>
                      <td>{formatOz(lot.pdOz)}</td>
                      <td>{formatOz(lot.rhOz)}</td>
                      <td>{formatUsd(lot.ceramicAmount)}</td>
                      <td>{formatUsd(lot.settledPayment)}</td>
                      <td className={`lot-hedge-var${varianceClass(lot.ptOzVariance)}`}>
                        {formatSignedOz(lot.ptOzVariance)}
                      </td>
                      <td className={`lot-hedge-var${varianceClass(lot.pdOzVariance)}`}>
                        {formatSignedOz(lot.pdOzVariance)}
                      </td>
                      <td className={`lot-hedge-var${varianceClass(lot.rhOzVariance)}`}>
                        {formatSignedOz(lot.rhOzVariance)}
                      </td>
                      <td className={`lot-hedge-var${varianceClass(lot.paymentVariance)}`}>
                        {formatSignedUsd(lot.paymentVariance)}
                      </td>
                      <td>{lot.invoiceCount ?? "—"}</td>
                      <td>{formatDate(lot.latestDate)}</td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>

        {hasMore && (
          <div className="lot-hedge-v2-more">
            <button
              type="button"
              className="lot-hedge-v2-more-btn"
              disabled={isLoadingMore}
              onClick={() =>
                loadLots({
                  text: query,
                  skip: offset,
                  append: true,
                  settlementStatus: statusFilter,
                  ptOzVariance: ptOzVarianceFilter,
                  pdOzVariance: pdOzVarianceFilter,
                  rhOzVariance: rhOzVarianceFilter,
                  paymentVariance: paymentVarianceFilter,
                })
              }
            >
              {isLoadingMore ? "Loading…" : "Load more"}
            </button>
          </div>
        )}
      </div>

      {showCreateSettlement && (
        <CreateSettlementModal
          onClose={closeCreateSettlement}
          onSuccess={(message) => {
            setToastParams({ type: "SUCCESS", message, id: Date.now() });
            loadLots({
              text: query,
              skip: 0,
              append: false,
              settlementStatus: statusFilter,
              ptOzVariance: ptOzVarianceFilter,
              pdOzVariance: pdOzVarianceFilter,
              rhOzVariance: rhOzVarianceFilter,
              paymentVariance: paymentVarianceFilter,
            });
          }}
          onError={(message) =>
            setToastParams({ type: "ERROR", message, id: Date.now() })
          }
        />
      )}

      {toastParams.message && (
        <Toast type={toastParams.type} message={toastParams.message} id={toastParams.id} />
      )}
    </>
  );
};

export default LotHedgeSettlementV2;
